/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { getIn, useFormikContext } from "formik";
import RegisterPhotos from "./RegisterPhotos";
import ButtonWithLoader from "../../../../../../../../common/components/ui/loading/ButtonLoading";
import { HiPlus, HiTrash } from "react-icons/hi2";
import AsyncSelect from "react-select/async";
import { useInspectionRegister } from "../customHook/useUnlinkVehicles";
import { getCompanyId } from "@/common/services/company.api";
import { formatPrice } from "@/modules/maintenances/components/forms/PriceMaintenance.helper";
import ComboBox from "@/common/components/form/ComboBox";
import { RECOVERY_REASON } from "@/types/renter.type";

type RegisterGridProps = {
  title?: string;
  registers: Array<any>;
  onRemoveRegister: (index: number) => void;
  onCloseModal?: () => void;
};

export default function InspectionRegister(props: RegisterGridProps) {
  const { title, registers, onRemoveRegister } = props;
  const formik = useFormikContext<any>();
  const companyId = getCompanyId();
  const [manualCollision, setManualCollision] = React.useState(false);

  // Pega o vehicleId do contexto Formik
  const vehicleId = getIn(formik.values, "vehicle._id");
  const renterId = getIn(formik.values, "renterId");
  const nameRenter = getIn(formik.values, "renterName");
  const withoutPhotos = getIn(formik.values, "withoutPhotos") || false;
  const returnReason = getIn(formik.values, "returnReason") || "";
  const optionRepossesion = getIn(formik.values, "optionRepossesion") || "";
  const changeVehicle = getIn(formik.values, "changeVehicle");
  const hasMissingPhotos = !withoutPhotos && registers.some((register) => !register?.content?.trim());
  const recoveryOptions = Object.entries(RECOVERY_REASON)
    .filter(([key]) => key !== "UNDEFINED")
    .map(([key, value]) => ({
      label: value,
      value: key,
    }));

  const {
    isLoading,
    disableButton,
    damagedPartsRows,
    addPartRow,
    removePartRow,
    updatePartRow,
    loadOptions,
    handleUnlinkVehicle,
  } = useInspectionRegister(registers, props.onCloseModal, manualCollision);

  const handleAddRegister = () => {
    formik.setFieldValue("registers", [
      ...registers,
      { type: "", content: "", verified: false },
    ]);
  };

  const handleWithoutPhotosChange = (checked: boolean) => {
    formik.setFieldValue("withoutPhotos", checked);

    // Se marcar "sem fotos", limpa os registros de fotos
    if (checked) {
      formik.setFieldValue("registers", []);
    } else {
      // Se desmarcar, restaura os registros padrão
      formik.setFieldValue("registers", [
        { type: "Frente do veículo", content: "" },
        { type: "Lateral direita do veículo", content: "" },
        { type: "Lateral esquerda do veículo", content: "" },
        { type: "Traseira do veículo", content: "" },
        { type: "Odômetro", content: "" },
      ]);
    }
  };

  const isSubmitDisabled =
    disableButton ||
    hasMissingPhotos ||
    (withoutPhotos && returnReason.trim() === "");
  const hasReasonError = !optionRepossesion || optionRepossesion === "UNDEFINED";
  const hasChangeVehicleError = typeof changeVehicle !== "boolean";
  const hasReturnReasonError = withoutPhotos && !returnReason.trim();

// Define um valor mínimo para considerar como colisão 
  const COLLISION_THRESHOLD = 1500;

  function parseSellPriceFromLabel(label: string): number {
    if (!label) return 0;

    const match = label.match(/\(R\$\s*([\d.,]+)\)/);
    if (!match?.[1]) return 0;

    const normalized = match[1].replace(/\./g, "").replace(",", ".");
    return Number(normalized) || 0;
  }

  function getOptionSellPrice(option: any): number {
    const direct = Number(option?.originalData?.sellPrice ?? 0);
    if (direct) return direct;

    const label = String(option?.label ?? "");
    return parseSellPriceFromLabel(label);
  }

  const totalPiecesValue = damagedPartsRows.reduce((acc, row) => {
    const option = row?.selectedOption;
    if (!option) return acc;

    const price = getOptionSellPrice(option);
    const qty = Number(row.quantity ?? 1) || 1;

    return acc + price * qty;
  }, 0);

  const autoCollision = totalPiecesValue >= COLLISION_THRESHOLD;
  const finalCollision = Boolean(manualCollision) || autoCollision;

  return (
    <section className="p-4">
      <div className="mt-8 space-y-6">
        {!companyId ? (
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
              Selecione uma cidade
            </h3>
            <p className="text-sm text-yellow-700">
              Por favor, selecione uma cidade para prosseguir com o recolhimento do veículo.
            </p>
          </div>
        ) : (
          <>
            {/* Checkbox "Sem fotos" */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={withoutPhotos}
                  onChange={(e) => handleWithoutPhotosChange(e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div>
                  <span className="text-base font-semibold text-gray-800">
                    Devolução sem fotos
                  </span>
                  <p className="text-sm text-gray-600 mt-1">
                    Marque esta opção se não houver fotos disponíveis para o registro de devolução
                  </p>
                </div>
              </label>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className={`rounded-lg border p-4 ${hasReasonError ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`}>
                <ComboBox
                  name="optionRepossesion"
                  label="Motivo do recolhimento"
                  options={recoveryOptions}
                  required
                  value={optionRepossesion}
                />
                {hasReasonError && (
                  <p className="text-sm text-red-600">
                    Selecione o motivo do recolhimento.
                  </p>
                )}
              </div>

              <div className={`rounded-lg border p-4 ${hasChangeVehicleError ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`}>
                <label className="block font-semibold text-gray-700">
                  O cliente vai trocar de moto? <span className="text-red-500">*</span>
                </label>
                <div className="mt-3 flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="changeVehicle"
                      checked={changeVehicle === true}
                      onChange={() => formik.setFieldValue("changeVehicle", true)}
                    />
                    <span>Sim</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="changeVehicle"
                      checked={changeVehicle === false}
                      onChange={() => formik.setFieldValue("changeVehicle", false)}
                    />
                    <span>Não</span>
                  </label>
                </div>
                {hasChangeVehicleError && (
                  <p className="mt-2 text-sm text-red-600">
                    Informe se o cliente vai trocar de moto.
                  </p>
                )}
              </div>
            </div>

            {/* Exibe RegisterPhotos apenas se "sem fotos" NÃO estiver marcado */}
            {!withoutPhotos && (
              <><RegisterPhotos
                title={title || "Inspeção de Veículo"}
                items={registers}
                vehicleId={vehicleId || ""}
                renterId={renterId}
                nameRenter={nameRenter}
                onAddItem={handleAddRegister}
                onRemoveItem={onRemoveRegister}
                updateStatus={undefined}
                titleTextArea={"Descreva o motivo da devolução:"}
                showOptionRepossession={false}
                showReturnReason={false} /><div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  {hasMissingPhotos && (
                    <div className="mb-4 rounded border border-red-200 bg-red-50 p-3">
                      <p className="text-sm text-red-700">
                        Todas as fotos do veículo são obrigatórias quando a opção
                        <strong> Devolução sem fotos </strong>
                        não está marcada.
                      </p>
                    </div>
                  )}
                  <h3 className="text-lg font-semibold mb-4 text-gray-700">
                    Registro de Avarias (Peças)
                  </h3>

                  <div className="space-y-3">
                    {damagedPartsRows.map((row, index) => (
                      <div
                        key={row.id}
                        className="flex flex-col md:flex-row gap-3 items-start md:items-center bg-white p-3 rounded shadow-sm"
                      >
                        <div className="flex-grow w-full md:w-auto">
                          <label className="text-xs font-semibold text-gray-500 mb-1 block">
                            Peça / Item
                          </label>
                          <AsyncSelect
                            cacheOptions={false}
                            defaultOptions
                            loadOptions={loadOptions}
                            value={row.selectedOption}
                            onChange={(option) => updatePartRow(index, "selectedOption", option)}
                            placeholder="Digite para buscar a peça..."
                            noOptionsMessage={() => "Nenhuma peça encontrada"}
                            loadingMessage={() => "Carregando..."}
                            className="text-sm"
                            styles={{
                              control: (base) => ({ ...base, minHeight: "38px" }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused ? "#dbeafe" : "white",
                                color: "black",
                              }),
                            }} />
                        </div>

                        <div className="w-full md:w-24">
                          <label className="text-xs font-semibold text-gray-500 mb-1 block">
                            Qtd.
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={row.quantity}
                            onChange={(e) => updatePartRow(
                              index,
                              "quantity",
                              parseInt(e.target.value) || 1
                            )}
                            className="w-full border border-gray-300 rounded px-2 py-2 text-sm h-[38px]" />
                        </div>

                        <div className="mt-4 md:mt-0">
                          <button
                            type="button"
                            onClick={() => removePartRow(index)}
                            className="text-red-500 hover:text-red-700 p-2 rounded hover:bg-red-50 transition-colors"
                            title="Remover item"
                          >
                            <HiTrash size={20} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addPartRow}
                    className="mt-4 flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 px-3 py-2 rounded hover:bg-blue-50 transition-colors"
                  >
                    <HiPlus /> Adicionar outra peça
                  </button>
                  {}
                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-blue-900">
                      Valor total das peças
                    </span>
                    <span className="text-sm font-bold text-blue-900">
                      R$ {formatPrice(totalPiecesValue || 0)}
                    </span>
                  </div>

                  <div className="mt-3 bg-white border border-gray-200 rounded p-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={finalCollision}
                        disabled={autoCollision}
                        onChange={(e) => setManualCollision(e.target.checked)}
                        className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-semibold text-gray-800">
                          Colisão
                        </span>
                        <p className="text-xs text-gray-600 mt-1">
                          {autoCollision
                            ? "Marcado automaticamente (valor das peças ≥ R$ 1.500,00)."
                            : "Marque se este recolhimento envolve colisão."}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </>
            )}

            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-700">
                {withoutPhotos ? (
                  <span className="flex items-center gap-2">
                    Descreva o motivo da devolução
                    <span className="text-red-500">*</span>
                  </span>
                ) : (
                  "Observações gerais da devolução"
                )}
              </label>

              {withoutPhotos && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r mb-2">
                  <p className="text-sm text-yellow-800 font-medium">
                    ⚠️ Como você optou por enviar sem fotos, é obrigatório descrever detalhadamente o motivo da devolução.
                  </p>
                </div>
              )}

              <textarea
                name="returnReason"
                value={returnReason}
                onChange={formik.handleChange}
                className={`border rounded p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 outline-none ${withoutPhotos && !returnReason.trim()
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300'
                  }`}
                placeholder={
                  withoutPhotos
                    ? "Descreva detalhadamente o motivo da devolução sem fotos..."
                    : "Descreva aqui outros detalhes da devolução..."
                }
              />

              {hasReturnReasonError && (
                <p className="text-red-500 text-sm">
                  Este campo é obrigatório quando a devolução é sem fotos
                </p>
              )}
            </div>

            <div className="flex justify-end pt-4 border-t">
              <ButtonWithLoader
                type="button"
                onClick={handleUnlinkVehicle}
                isLoading={isLoading}
                disabled={isSubmitDisabled}
              >
                Confirmar Desvinculação
              </ButtonWithLoader>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
